import React from "react";
import PropTypes from "prop-types";
import { accountType, userType, documentPropType, locationType } from "custom-prop-types";
import { Notifications } from "components/notifications";
import { browserHistory, Link } from "react-router";
import { Select } from "components/common/select";
import { SettingsModal } from "components/settings/settings-modal";
import { onHeaderSearch } from "utils/key-press";
import styles from "components/app-header.scss";
import logoImageUrl from "squirrelstreet-logo-header.png";
import FaCog from "react-icons/fa/cog";
import FaBell from "react-icons/fa/bell-o";
import FaSearch from "react-icons/fa/search";

export const AppHeader = ({
  user,
  accounts,
  exportFailedCount,
  isNotificationsMode,
  isNotificationsViewed,
  isSettingsMode,
  location,
  onAddSearchQuery,
  onChangeSelectedAccount,
  onEndNotificationsMode,
  onEndSettingsMode,
  onLogout,
  onStartNotificationsMode,
  onStartSettingsMode,
  onUpdateSearchQuery,
  pendingDocuments,
  queuedCount,
  searchQueryString,
  selectedAccount
}) => {
  const notificationsLength = pendingDocuments.length + queuedCount + exportFailedCount;
  const onSearch = (query) => {
    if (location.pathname === `/accounts/${selectedAccount.slug}/documents/all`) {
      return onAddSearchQuery(query);
    }
    browserHistory.push(`/accounts/${selectedAccount.slug}/documents/all`);
    return onAddSearchQuery(query);
  };
  return (
    <div className={ styles.container }>
      { renderBetaBanner() }
      <div className={ styles.content }>
        <img alt="Squirrel Street logo" className={ styles.logo } src={ logoImageUrl } />
        {navigationPanel(selectedAccount, pendingDocuments.length) }
        <div className={ styles["account-details"] }>
          <div className={ styles.search }>
            <input
              className={ styles.fields }
              onChange={ (e) => onUpdateSearchQuery(e.target.value) }
              onKeyDown={ (e) => onHeaderSearch(e, searchQueryString, onAddSearchQuery, selectedAccount, location) }
              placeholder={ "Search..." }
              value={ searchQueryString }
            />
            <FaSearch className={ styles["search-button"] } onClick={ () => onSearch(searchQueryString) } />
          </div>
          <div
            className={ renderNotificationsStyle(isNotificationsViewed, notificationsLength) }
            onClick={ onStartNotificationsMode }
          >
            <FaBell size={ 15 } />
          </div>
          <Notifications
            accountSlug={ selectedAccount.slug }
            exportFailedCount={ exportFailedCount }
            isNotificationsMode={ isNotificationsMode }
            onEndNotificationsMode={ onEndNotificationsMode }
            pendingDocumentsCount={ pendingDocuments.length }
            queuedCount={ queuedCount }
          />
          <div className={ `${styles.dropdown} overview6` } onClick={ onStartSettingsMode }>
            { user.firstName } { user.lastName } &nbsp;
            <FaCog size={ 15 } />
          </div>
          <SettingsModal
            isSettingsMode={ isSettingsMode }
            onEndSettingsMode={ onEndSettingsMode }
            onLogout={ onLogout }
            selectedAccount={ selectedAccount }
          />
          { renderSelectContainer(accounts, onChangeSelectedAccount, selectedAccount) }
        </div>
      </div>
    </div>
  );
};

AppHeader.propTypes = {
  accounts: PropTypes.arrayOf(accountType).isRequired,
  exportFailedCount: PropTypes.number.isRequired,
  isNotificationsMode: PropTypes.bool.isRequired,
  isNotificationsViewed: PropTypes.bool.isRequired,
  isSettingsMode: PropTypes.bool.isRequired,
  location: locationType.isRequired,
  onAddSearchQuery: PropTypes.func.isRequired,
  onChangeSelectedAccount: PropTypes.func.isRequired, // (string) => void
  onEndNotificationsMode: PropTypes.func.isRequired,
  onEndSettingsMode: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired, // () => void
  onStartNotificationsMode: PropTypes.func.isRequired,
  onStartSettingsMode: PropTypes.func.isRequired,
  onUpdateSearchQuery: PropTypes.func.isRequired,
  pendingDocuments: PropTypes.arrayOf(documentPropType).isRequired,
  queuedCount: PropTypes.number.isRequired,
  searchQueryString: PropTypes.string.isRequired,
  selectedAccount: accountType.isRequired,
  user: userType.isRequired
};

const navigationPanel = (selectedAccount, pendingDocumentsCount) => (
  <div className={ styles["navigation-panel__container"] }>
    <ul>
      <li>
        <Link
          activeClassName={ styles["navigation-panel__selected"] }
          className="overview1"
          to={ `/accounts/${selectedAccount.slug}/dashboard` }
        >
          Dashboard
        </Link>
      </li>
      <li>
        <Link
          activeClassName={ styles["navigation-panel__selected"] }
          className="overview2 upload1 manual1"
          to={ `/accounts/${selectedAccount.slug}/upload` }
        >
          Submit {renderPendingCount(pendingDocumentsCount)}
        </Link>
      </li>
      <li>
        <Link
          activeClassName={ styles["navigation-panel__selected"] }
          className="overview3 documents1"
          to={ `/accounts/${selectedAccount.slug}/documents` }
        >
          View and export
        </Link>
      </li>
      <li>
        <Link
          activeClassName={ styles["navigation-panel__selected"] }
          className="overview4 automate1"
          to={ `/accounts/${selectedAccount.slug}/automate` }
        >
          Automate
        </Link>
      </li>
      <li>
        <Link
          activeClassName={ styles["navigation-panel__selected"] }
          className="overview5"
          to={ `/accounts/${selectedAccount.slug}/reports` }
        >
          Report
        </Link>
      </li>

    </ul>
  </div>
);

const mapAccounts = (accounts) => accounts.map(accountMapper);
const accountMapper = (account) => (
  { key: account.id, name: account.label, info: account.slug }
);

const renderSelectContainer = (accounts, onChangeSelectedAccount, selectedAccount) => {
  if (accounts.length === 1) {
    return null;
  }
  return (
    <div className={ styles["select-container"] }>
      <Select
        data={ mapAccounts(accounts) }
        onChange={ (item) => { onChangeSelectedAccount(item.key, item.info); } }
        value={ accountMapper(selectedAccount) }
      />
    </div>
  );
};

const renderPendingCount = (pendingDocumentsCount) => {
  if (pendingDocumentsCount === 0) {
    return null;
  }
  return `(${pendingDocumentsCount})`;
};

const renderNotificationsStyle = (isNotificationsViewed, notificationsLength) => {
  if (!isNotificationsViewed && notificationsLength > 0) {
    return `${styles.unviewed} ${styles["notifications-button"]}`;
  }
  return styles["notifications-button"];
};

const renderBetaBanner = () => {
  if (OAUTH_CLIENT_ID === "SQRLST_WEB_APP_NEWUI_BETA") {
    return <div className={ styles["beta-banner"] } />;
  }
  return null;
};



// WEBPACK FOOTER //
// ./src/js/components/app-header.jsx